import { NodeType } from 'modules/workflows/types';
import { AnyStyledComponent } from 'styled-components';
import {
  ABSplit,
  AddContact,
  AiTileCategory,
  AttributeSplit,
  CallWebhook,
  ChangeContactField,
  ChannelOpt,
  Delay,
  Embedded24,
  FbAudience,
  FieldsMatch,
  Hold,
  HoldUntilReply,
  ModifyChannel,
  Note,
  RemoveContact,
  SendEmail,
  SendInApp,
  SendPush,
  SendSMS,
  SendWebPush,
  SendWhatsApp,
  StartFilled,
  Tileset,
  TriggerWorkflow,
} from 'ui/icons';
import { createTileCategory } from 'ui/icons/utils';
import { colors } from 'ui/theme';
import { NodeStyle } from 'ui/workflows/nodeTilePanel';

import { NODE_TYPES } from './nodeConstants';
import { NODE_SECTION_TYPES, NODE_TYPE_TO_SECTION_MAP } from './sectionConstants';

type NodeColor = Omit<NodeStyle, 'icon'>;

const sectionColors = {
  [NODE_SECTION_TYPES.DELAY]: {
    accentColor: colors.salmon50,
  },
  [NODE_SECTION_TYPES.FILTER_AND_FLOW]: {
    accentColor: colors.purple50,
  },
  [NODE_SECTION_TYPES.MESSAGES]: {
    accentColor: colors.cyan100,
  },
  [NODE_SECTION_TYPES.USER_UPDATES]: {
    accentColor: colors.green50,
  },
  [NODE_SECTION_TYPES.INTEGRATIONS]: {
    accentColor: colors.flamingo100,
  },
  LEGACY: {
    accentColor: colors.red50,
  },
};

export const NODE_COLORS = {
  [NODE_TYPES.START]: {
    accentColor: colors.forest100,
  },
  [NODE_TYPES.NOTE]: {
    accentColor: colors.mustard100,
  },
  [NODE_TYPES.TILESET]: {
    accentColor: colors.grey35,
  },

  // legacy nodes that need similar color to section still
  [NODE_TYPES.MODIFY_MESSAGE_TYPE]: sectionColors[NODE_SECTION_TYPES.USER_UPDATES],
  [NODE_TYPES.UPDATE_CONTACT_WEBHOOK]: sectionColors[NODE_SECTION_TYPES.USER_UPDATES],
  [NODE_TYPES.REMOVE_CONTACT]: sectionColors[NODE_SECTION_TYPES.USER_UPDATES],
  [NODE_TYPES.REMOVE_USER_FROM_FB]: sectionColors[NODE_SECTION_TYPES.INTEGRATIONS],

  ...sectionColors,
};

const AiStyledTileCategory = createTileCategory(AiTileCategory as AnyStyledComponent);
export const NODE_ICONS = {
  [NODE_TYPES.AB_SPLIT]: { icon: ABSplit },
  [NODE_TYPES.ADD_CONTACT]: { icon: AddContact },
  [NODE_TYPES.ADD_USER_TO_FB]: { icon: FbAudience },
  [NODE_TYPES.ATTRIBUTE_SPLIT]: { icon: AttributeSplit },
  [NODE_TYPES.CALL_WEBHOOK]: { icon: CallWebhook },
  [NODE_TYPES.CHANGE_CONTACT_FIELD]: { icon: ChangeContactField },
  [NODE_TYPES.CHANNEL_OPT]: { icon: ChannelOpt, iconCategory: AiStyledTileCategory },
  [NODE_TYPES.DELAY]: { icon: Delay },
  [NODE_TYPES.FIELDS_MATCH]: { icon: FieldsMatch },
  [NODE_TYPES.HOLD]: { icon: Hold },
  [NODE_TYPES.HOLD_UNTIL_REPLY]: { icon: HoldUntilReply },
  [NODE_TYPES.MODIFY_CHANNEL_SUBSCRIPTION]: { icon: ModifyChannel },
  [NODE_TYPES.MODIFY_MESSAGE_TYPE]: { icon: ModifyChannel },
  [NODE_TYPES.NOTE]: { icon: Note },
  [NODE_TYPES.REMOVE_CONTACT]: { icon: RemoveContact },
  [NODE_TYPES.REMOVE_USER_FROM_FB]: { icon: FbAudience },
  [NODE_TYPES.SEND_EMAIL]: { icon: SendEmail },
  [NODE_TYPES.SEND_EMBEDDED]: { icon: Embedded24 },
  [NODE_TYPES.SEND_IN_APP]: { icon: SendInApp },
  [NODE_TYPES.SEND_PUSH]: { icon: SendPush },
  [NODE_TYPES.SEND_SMS]: { icon: SendSMS },
  [NODE_TYPES.SEND_WEB_PUSH]: { icon: SendWebPush },
  [NODE_TYPES.SEND_WHATSAPP]: { icon: SendWhatsApp },
  [NODE_TYPES.START]: { icon: StartFilled },
  [NODE_TYPES.TILESET]: { icon: Tileset },
  [NODE_TYPES.TRIGGER_WORKFLOW]: { icon: TriggerWorkflow },
  [NODE_TYPES.UPDATE_CONTACT_WEBHOOK]: { icon: ChangeContactField },
};

const getNodeColorConfig = (nodeType: NodeType): NodeColor =>
  NODE_COLORS[nodeType] || NODE_COLORS[NODE_TYPE_TO_SECTION_MAP[nodeType]];

interface NodeStyleMap {
  /** The style associated with this node type */
  [key: NodeType]: NodeStyle;
}

export const NODE_STYLE_CONFIGS: NodeStyleMap = Object.values(NODE_TYPES).reduce(
  (acc, nodeType) => ({
    ...acc,
    [nodeType]: {
      ...getNodeColorConfig(nodeType),
      ...NODE_ICONS[nodeType],
    },
  }),
  {},
);
